import React from "react";
import { MathJax, MathJaxContext } from "better-react-mathjax";

interface MathRendererProps {
  text: string;
}

const mathJaxConfig = {
  loader: { load: ["input/tex", "input/asciimath", "output/chtml"] },
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
  },
  asciimath: {
    delimiters: [["`", "`"]],
  },
  options: {
    enableMenu: false,
  },
};

const MathRenderer: React.FC<MathRendererProps> = ({ text }) => {
  if (!text) return null;

  const renderBoldText = (content: string) => {
    const parts = content.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const preprocessText = (content: string) => {
    if (!content) return "";
    
    // Check if it already has delimiters ($ or \( or ` or \[)
    const hasDelimiters = /\$|\\\(|\\\[|`/.test(content);
    
    if (!hasDelimiters && /[\^|_]/.test(content)) {
      // Improved regex to capture more complex math expressions including spaces and operators
      // like x^2 + 2 = 0
      return content.replace(/((?:[0-9a-zA-Z]+[\^|_][0-9a-zA-Z()]+|\b[xyz][0-9a-zA-Z]*\b)(?:\s*[=<>!+\-*/]+\s*[0-9a-zA-Z^/_*\-+()]+)*)/g, "`$1` ");
    }

    return content;
  };

  const renderLines = () => {
    const lines = text.split("\n");
    return lines.map((line, i) => {
      if (line.trim() === "") return <br key={i} />;

      // Handle Markdown-style headers
      if (line.startsWith("### ")) return <h3 key={i}>{renderBoldText(line.slice(4))}</h3>;
      if (line.startsWith("## ")) return <h2 key={i}>{renderBoldText(line.slice(3))}</h2>;
      if (line.startsWith("# ")) return <h1 key={i}>{renderBoldText(line.slice(2))}</h1>;

      const processedLine = preprocessText(line);
      const content = renderBoldText(processedLine);

      return (
        <div key={i} className="math-line" style={{ margin: "0.2rem 0" }}>
          {/* We wrap in MathJax to allow processing of delimiters like $, \(, or ` */}
          <MathJax dynamic>{content}</MathJax>
        </div>
      );
    });
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="math-renderer-wrapper">
        {renderLines()}
      </div>
    </MathJaxContext>
  );
};

export default MathRenderer;
